import { useQuery } from "@apollo/client";
import {
  Alert,
  Col,
  ComboBox,
  Text,
  TextArea,
  TextInput,
} from "@fluidattacks/design";
import type { IItem } from "@fluidattacks/design";
import { get, isEmpty, isNull, isUndefined, some } from "lodash";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IEnvironmentUrlProps, IFile } from "./types";

import { GET_FILES, GET_GROUP_ENVS } from "../../../../../queries";
import {
  CLOUD_NAME_VALUES,
  MOBILE_EXTENSIONS,
  URL_TYPE_VALUES,
} from "../utils";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IEnvironment {
  id: string;
  cloudName: string | null;
  url: string;
  urlType: string;
}

const EnvironmentUrl = ({
  groupName,
  control,
  formState: { errors, touchedFields },
  getValues,
  register,
  watch,
}: IEnvironmentUrlProps): JSX.Element => {
  const { t } = useTranslation();
  const [cloudName, urlType, url] = getValues(["cloudName", "urlType", "url"]);
  const { APK, CSPM } = URL_TYPE_VALUES;
  const { AWS, AZURE, GCP } = CLOUD_NAME_VALUES;

  const { data: environmentsData } = useQuery(GET_GROUP_ENVS, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load Git roots environments", error);
      });
    },
    variables: { groupName },
  });

  const { data } = useQuery(GET_FILES, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning("An error occurred loading group files", error);
      });
    },
    variables: { groupName },
  });

  const filesDataset = useMemo(
    (): IFile[] =>
      isUndefined(data) || isEmpty(data) || isNull(data.resources.files)
        ? []
        : (data.resources.files as IFile[]).filter((resource): boolean =>
            MOBILE_EXTENSIONS.some((elm): boolean =>
              resource.fileName.endsWith(elm),
            ),
          ),
    [data],
  );
  const selectOptions = filesDataset.map(
    (file): IItem => ({ name: file.fileName, value: file.fileName }),
  );

  const isDuplicate = useMemo((): boolean => {
    const environments = get(environmentsData, "group.gitEnvironmentUrls", []);
    const hasValidValues = !isEmpty(urlType) && !isEmpty(url);

    if (!Array.isArray(environments) || !hasValidValues) {
      return false;
    }

    return some(environments, (env: IEnvironment): boolean => {
      const isUrlTypeMatch = env.urlType === urlType;
      const isUrlMatch = env.url === url;

      if (urlType === CSPM) {
        return isUrlTypeMatch && env.cloudName === cloudName && isUrlMatch;
      }

      return isUrlTypeMatch && isUrlMatch;
    });
  }, [environmentsData, urlType, cloudName, url, CSPM]);

  const duplicateAlert = isDuplicate ? (
    <Alert variant={"warning"}>
      <Text size={"md"}>
        {t("group.scope.git.addEnvironment.duplicateEnvironmentMessage")}
      </Text>
    </Alert>
  ) : null;

  const urlInput: JSX.Element =
    urlType === APK ? (
      <ComboBox
        control={control}
        items={selectOptions}
        label={t("group.scope.git.addEnvironment.apk")}
        name={"url"}
        required={true}
      />
    ) : (
      <TextInput
        error={errors.url?.message}
        isTouched={"url" in touchedFields}
        isValid={!("url" in errors)}
        label={t("group.scope.git.addEnvironment.urlLabel")}
        name={"url"}
        register={register}
        required={true}
        tooltip={t("group.scope.git.addEnvironment.urlTooltip")}
      />
    );

  if (urlType === CSPM && cloudName !== undefined) {
    switch (cloudName) {
      case AWS:
        return (
          <Col>
            <TextInput
              error={errors.url?.message}
              isTouched={"url" in touchedFields}
              isValid={!("url" in errors)}
              label={t("group.scope.git.addEnvironment.awsRoleArn")}
              name={"url"}
              register={register}
              required={true}
            />
            {duplicateAlert}
          </Col>
        );
      case GCP:
        return (
          <Fragment>
            <TextInput
              error={errors.url?.message}
              isTouched={"url" in touchedFields}
              isValid={!("url" in errors)}
              label={t("group.scope.git.addEnvironment.gcpProjectId")}
              name={"url"}
              register={register}
              required={true}
            />
            <TextArea
              error={errors.gcpPrivateKey?.message}
              isTouched={"gcpPrivateKey" in touchedFields}
              isValid={!("gcpPrivateKey" in errors)}
              label={t("group.scope.git.addEnvironment.gcpPrivateKey")}
              maxLength={20000}
              name={"gcpPrivateKey"}
              register={register}
              required={true}
              watch={watch}
            />
            {duplicateAlert}
          </Fragment>
        );
      case AZURE:
        return (
          <Fragment>
            <TextInput
              error={errors.url?.message}
              isTouched={"url" in touchedFields}
              isValid={!("url" in errors)}
              label={t("group.scope.git.addEnvironment.azureSubscriptionId")}
              name={"url"}
              register={register}
              required={true}
            />
            <TextInput
              error={errors.azureClientId?.message}
              isTouched={"azureClientId" in touchedFields}
              isValid={!("azureClientId" in errors)}
              label={t("group.scope.git.addEnvironment.azureClientId")}
              name={"azureClientId"}
              register={register}
              required={true}
            />
            <TextInput
              error={errors.azureClientSecret?.message}
              isTouched={"azureClientSecret" in touchedFields}
              isValid={!("azureClientSecret" in errors)}
              label={t("group.scope.git.addEnvironment.azureClientSecret")}
              name={"azureClientSecret"}
              register={register}
              required={true}
            />
            <TextInput
              error={errors.azureTenantId?.message}
              isTouched={"azureTenantId" in touchedFields}
              isValid={!("azureTenantId" in errors)}
              label={t("group.scope.git.addEnvironment.azureTenantId")}
              name={"azureTenantId"}
              register={register}
              required={true}
            />
            {duplicateAlert}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  }

  return <Col>{urlInput}</Col>;
};

export { EnvironmentUrl };
